import React from 'react';
import { ChevronDown, Mail, Calendar, MapPin, Clock, FileText } from 'lucide-react';

const Website = () => {
  // Add smooth scrolling behavior to the whole document
  React.useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Roboto',sans-serif]">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img 
                src="/CIMHS Logo.png"
                alt="CIMHS Logo"
                className="h-12"
              />
            </div>
            <div className="flex items-center space-x-8">
              <a href="#hero" className="text-black hover:underline">Home</a>
              <a href="#upcoming-event" className="text-black hover:underline">Advocacy Day</a>
              <a href="#current-initiative" className="text-black hover:underline">The Bills</a>
              <a href="#event-speakers" className="text-black hover:underline">Speakers</a>
              <a href="#who-we-are" className="text-black hover:underline">About</a>
              <a href="#contact-us" className="text-black hover:underline">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative flex items-center justify-center bg-[#4B9CD3] mt-16 min-h-[800px]">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/Background Image.jpg"
            alt="Background"
            className="w-full h-full object-cover object-center"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-black/30" /> {/* Overlay for better text readability */}
        </div>

        {/* Blue Banner with White Border */}
        <div className="relative z-10 w-full bg-[#4B9CD3]/90 py-12 border-y-4 border-white my-32">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col items-center text-center">
              <h1 className="text-6xl font-medium mb-6 font-['Montserrat',Verdana,Geneva,sans-serif] text-white">
                Coalition for an Integrated Mental Health System
              </h1>

              <p className="text-2xl text-white mb-12 max-w-2xl">
                Advocating for meaningful change in mental health policy
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <ChevronDown className="w-12 h-12 text-white animate-bounce drop-shadow-lg" />
        </div>
      </section>
      {/* Upcoming Event Section */}
      <section id="upcoming-event" className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="bg-[#4B9CD3] p-10 rounded-2xl shadow-2xl text-white">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="md:w-2/3">
                <h2 className="text-4xl font-bold mb-6 font-['Montserrat',Verdana,Geneva,sans-serif]">Join Us: Treatment, Not Incarceration Advocacy Day</h2>
                <div className="space-y-4 text-lg">
                  <div className="flex items-center">
                    <Calendar className="w-6 h-6 mr-4" />
                    <span>Thursday, February 27, 2025</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-6 h-6 mr-4" />
                    <span>10:00 AM – 3:00 PM</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-6 h-6 mr-4" />
                    <span>Room 437, Massachusetts State House, 24 Beacon Street, Boston, MA 02133</span>
                  </div>
                </div>
              </div>
              <div className="md:w-1/3 mt-8 md:mt-0 flex flex-col items-center">
                <div className="bg-[#13294B] p-6 rounded-xl w-full">
                  <h3 className="text-xl font-bold mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">What to Expect:</h3>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Meet with legislators</li>
                    <li>Share your story</li>
                    <li>Network with advocates</li>
                    <li>Learn about policy reform</li>
                  </ul>
                </div>
                <a href="https://docs.google.com/forms/d/e/1FAIpQLSdGGL6tPEXMyFHuVHHG7T0kMDw1spC6sDa2AQY0Nta0P3JAng/viewform?usp=sharing" target="_blank" rel="noopener noreferrer" className="w-full">
                  <button className="mt-8 bg-white text-[#4B9CD3] px-8 py-4 rounded-lg text-2xl font-bold hover:bg-[#F8F8F8] transition-colors shadow-lg w-full">
                    Sign Up Now!
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Initiative Section */}
      <section id="current-initiative" className="py-16 px-4 bg-[#13294B] text-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl font-bold mb-8 font-['Montserrat',Verdana,Geneva,sans-serif]">
            SD.1634/HD.873: An Act Relative to Treatment, Not Incarceration
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold mb-3 font-['Montserrat',Verdana,Geneva,sans-serif]">The Problem:</h4>
                <p className="text-white">People on probation can be reincarcerated for a single positive drug test, even when they are actively engaged in treatment. This punitive approach contradicts medical understanding of addiction as a chronic disease and disrupts the recovery process.</p>
              </div>
              <div>
                <h4 className="text-xl font-bold mb-3 font-['Montserrat',Verdana,Geneva,sans-serif]">The Solution:</h4>
                <p className="text-white">This bill would prevent a positive drug test from being considered a probation violation if the person on probation has a substance use disorder and:</p>
                <ul className="text-white list-disc list-inside space-y-2 mt-2 mb-2">
                  <li>is engaged in a treatment plan or is making reasonable efforts to receive treatment</li>
                  <li>or, is transitioning treatment plans under medical supervision</li>
                  <li>or, has completed a treatment plan and has no other probation violations.</li>
                </ul>
                <p className="text-white">Courts would maintain the authority to mandate substance use treatment, including for individuals who have relapsed after completing a treatment program.</p>
              </div>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-3 font-['Montserrat',Verdana,Geneva,sans-serif]">Why This Matters:</h4>
              <div className="bg-[#4B9CD3] p-6 rounded-xl">
                <ul className="space-y-4">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                    Addiction is a chronic disease; relapse is part of recovery.
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                    Incarceration is associated with a 120x increased risk of overdose death<a href="https://www.nejm.org/doi/full/10.1056/NEJMsa064115" className="text-white"><sup>1</sup></a><a href="https://www.mass.gov/doc/chapter-55-data-brief-2017/download#:~:text=Key%20Finding%3A%20Compared%20to%20the%20rest%20of%20the%20adult%20population,from%20Massachusetts%20prisons%20and%20jails" className="text-white"><sup>2</sup></a>.
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                    Incarceration costs over $100,000 per year. Unnecessary incarceration is a waste of taxpayer money.
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                    Incarceration disrupts treatment, employment, and family connections crucial for recovery.
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-6 bg-[#4B9CD3] rounded-xl">
            <h4 className="text-xl font-bold mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Bill Details</h4>
            <p className="text-white mb-4">
              This legislation amends Chapter 276 of the Massachusetts General Laws to prevent courts from incarcerating individuals solely for positive drug tests when they are actively participating in treatment programs. The bill is supported by leading medical and advocacy organizations including the Massachusetts Medical Society and the ACLU, who emphasize that treatment-based approaches are more effective and humane than punitive measures.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="/Bill HD PDF.pdf" 
                className="flex items-center bg-[#13294B] px-4 py-2 rounded-lg hover:bg-[#0F1F3A] transition-colors"
              >
                <FileText className="w-5 h-5 mr-2" />
                View House Bill (HD.873)
              </a>
              <a 
                href="/Bill SD PDF.pdf" 
                className="flex items-center bg-[#13294B] px-4 py-2 rounded-lg hover:bg-[#0F1F3A] transition-colors"
              >
                <FileText className="w-5 h-5 mr-2" />
                View Senate Bill (SD.1634)
              </a>
              <a 
                href="/CIMHS Memo For Legislators.pdf" 
                className="flex items-center bg-[#13294B] px-4 py-2 rounded-lg hover:bg-[#0F1F3A] transition-colors"
              >
                <FileText className="w-5 h-5 mr-2" />
                CIMHS Policy Memo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-16 px-4 bg-[#F8F8F8]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-[#4B9CD3] mb-12 text-center font-['Montserrat',Verdana,Geneva,sans-serif]">Event Schedule</h2>
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="space-y-8">
              <div>
                <h4 className="text-xl font-bold mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Morning</h4>
                <ul className="list-disc list-inside space-y-4 text-lg text-[#151515]">
                  <li>10:00 AM - 10:10 AM: Welcome, Introductions, and Event Overview</li>
                  <li>10:10 AM - 10:35 AM: Keynote: Lovelee Harvey</li>
                  <li>10:35 AM - 10:50 AM: Student Perspective</li>
                  <li>10:50 AM - 10:55 AM: Last Notes, Disclosures, Expectations For The Day</li>
                  <li><i>Transition/Break Time</i></li>
                  <li>11:00 AM - 11:30 AM: Panel Presentation On HD.873/SD.1634</li>
                  <li>11:30 AM - 11:55 AM: Open Q&A</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Lunch</h4>
                <ul className="list-disc list-inside space-y-4 text-lg text-[#151515]">
                  <li>12:00 PM - 1:00 PM: Lunch, featuring:</li>
                  <li className="ml-8">Advocacy with Staffers</li>
                  <li className="ml-8">How-to-Advocate Training from the ACLU</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Afternoon</h4>
                <ul className="list-disc list-inside space-y-4 text-lg text-[#151515]">
                  <li>1:00 PM - 2:45 PM: Advocacy with Legislators</li>
                  <li>2:45 PM - 3:00 PM: Wrap-up Session</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speaker Section */}
      <section id="event-speakers" className="py-8 px-4 bg-[#F8F8F8]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-[#4B9CD3] mb-8 text-center font-['Montserrat',Verdana,Geneva,sans-serif]">Event Speakers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#13294B] font-['Montserrat',Verdana,Geneva,sans-serif]">Lovelee Harvey</h3>
                  <p className="text-lg text-[#151515]">Keynote Speaker</p>
                </div>
              </div>
              <p className="text-lg text-[#151515] leading-relaxed">
              Lovelee Harvey is a recovery coach at the MGH Bridge Clinic, providing immediate support to people with substance use disorders, particularly those recently released from incarceration. Drawing from his own experiences, he emphasizes the importance of meeting individuals where they are—both physically and emotionally—during vulnerable transition periods. He specializes in what's known as a "warm handoff," personally guiding people into treatment rather than leaving them to navigate it alone. His work focuses on breaking down the barriers that prevent people from seeking help, ensuring they receive compassionate, non-judgmental care at critical moments.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#13294B] font-['Montserrat',Verdana,Geneva,sans-serif]">Cindy Friedman</h3>
                  <p className="text-lg text-[#151515]">State Senator</p>
                </div>
              </div>
              <p className="text-lg text-[#151515] leading-relaxed">
              Senator Cindy Friedman has served Massachusetts' Fourth Middlesex District since 2017, bringing a background in education, technology, and public service to her role. She has played a key part in advancing policies that protect vulnerable populations, expand access to healthcare, and support workers' rights, including securing a $15 minimum wage and paid family leave. Her work on mental health and criminal justice reform has shaped policies that prioritize treatment over incarceration, and she has been instrumental in directing funding to community initiatives. With deep roots in her district, she continues to push for legislation that strengthens civil rights, workforce development, and healthcare accessibility.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#13294B] font-['Montserrat',Verdana,Geneva,sans-serif]">Lisa Newman-Polk</h3>
                  <p className="text-lg text-[#151515]">Lawyer, Social Worker, Advocate</p>
                </div>
              </div>
              <p className="text-lg text-[#151515] leading-relaxed">
              Lisa Newman-Polk's career spans both law and clinical social work, allowing her to challenge the intersection of the criminal legal system and addiction treatment. After starting as a public defender, she became a mental health clinician in Massachusetts' maximum-security prison, where she saw firsthand how addiction was treated with punishment rather than care. This led her to advocate for systemic reforms, including arguing the high-profile case Commonwealth v. Eldred, which questioned the constitutionality of jailing individuals for relapse. Today, she represents people at parole hearings and works on legislation to end incarceration for drug relapse, combining legal expertise with a deep understanding of addiction science.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#13294B] font-['Montserrat',Verdana,Geneva,sans-serif]">Rohit Abraham</h3>
                  <p className="text-lg text-[#151515]">Addiction Psychiatrist</p>
                </div>
              </div>
              <p className="text-lg text-[#151515] leading-relaxed">
              Dr. Rohit Abraham specializes in addiction medicine and emergency psychiatry, working at the intersection of mental health and substance use treatment. As an Addiction Medicine Fellow, he rotates across multiple hospitals, addressing complex cases that require both psychiatric and medical expertise. At Boston Medical Center, he founded the PRIME Clinic, which integrates primary care into psychiatric services to improve health outcomes for people with severe mental illness and addiction. His background in public health policy, combined with his hands-on clinical work, informs his efforts to bridge gaps in care for marginalized populations.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#13294B] font-['Montserrat',Verdana,Geneva,sans-serif]">Mike Jellison</h3>
                  <p className="text-lg text-[#151515]">Recovery Coach</p>
                </div>
              </div>
              <p className="text-lg text-[#151515] leading-relaxed">
              Mike Jellison is part of the BHCHP Street Team, which provides medical and substance use treatment directly to people living on the streets of Boston. For the past seven years, he has served as a recovery coach, using his own experiences to build trust with individuals who often avoid traditional healthcare settings. His role goes beyond offering immediate support—he follows patients from the streets to clinics, hospitals, and housing programs, ensuring they remain connected to care. He believes in recognizing the full humanity of the people he works with, emphasizing that they have stories, ambitions, and pasts that extend beyond their current hardships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="who-we-are" className="py-16 px-4 bg-[#F8F8F8]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-[#4B9CD3] mb-12 text-center font-['Montserrat',Verdana,Geneva,sans-serif]">Who We Are</h2>
          
          {/* Mission and Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-[#4B9CD3] p-6 rounded-xl text-white">
              <h3 className="text-xl font-bold mb-3 font-['Montserrat',Verdana,Geneva,sans-serif]">Our Mission</h3>
              <p>Advocating for evidence-based policy reform in mental health and substance use treatment.</p>
            </div>
            <div className="bg-[#13294B] p-6 rounded-xl text-white">
              <h3 className="text-xl font-bold mb-3 font-['Montserrat',Verdana,Geneva,sans-serif]">Our Vision</h3>
              <p>Creating an integrated mental health system that prioritizes treatment over incarceration.</p>
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold text-[#13294B] mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Leadership</h3>
                <ul className="space-y-2 text-[#151515]">
                  <li>Benjamin Gorman - Harvard Medical School</li>
                  <li>Camryn Slosky - Tufts University School of Medicine</li>
                </ul>

                <h3 className="text-xl font-bold text-[#13294B] mt-6 mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Substance Use and Harm Reduction</h3>
                <ul className="space-y-2 text-[#151515]">
                  <li>Katie Ahn - Tufts University</li>
                  <li>Anshul Puli - Harvard Medical School</li>
                  <li>Vance Boyd - Tufts University</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-[#13294B] mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Incarceration</h3>
                <ul className="space-y-2 text-[#151515]">
                  <li>Jane Smagulova - Tufts University</li>
                  <li>Krishni Satchi - Boston University School of Medicine</li>
                  <li>Arthi Bobbala - Boston University School of Medicine</li>
                  <li>Fiona Wu - Mass General Brigham</li>
                </ul>

                <h3 className="text-xl font-bold text-[#13294B] mt-6 mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Guardianship Reform</h3>
                <ul className="space-y-2 text-[#151515]">
                  <li>Nell Mermin-Bunnell - Harvard Kennedy School</li>
                  <li>Aarushi Solanki - Harvard Law School</li>
                  <li>Gabrielle Lewis - Harvard Law School</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-[#13294B] mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Housing</h3>
                <ul className="space-y-2 text-[#151515]">
                  <li>Sophie Roe - Harvard Chan School of Public Health</li>
                  <li>RJ Russel - Harvard Medical School</li>
                  <li>Lorraine Pereira - Harvard Medical School</li>
                  <li>Anna Rusignuolo - Harvard Kennedy School</li>
                </ul>

                <h3 className="text-xl font-bold text-[#13294B] mt-6 mb-4 font-['Montserrat',Verdana,Geneva,sans-serif]">Operations</h3>
                <ul className="space-y-2 text-[#151515]">
                  <li>Benjamin Colao - Tufts University (Logistics)</li>
                  <li>Ruhi Kanwar - Harvard Medical School (Advertising)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-us" className="py-16 px-4 bg-[#13294B]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-8 font-['Montserrat',Verdana,Geneva,sans-serif]">Contact Us</h2>
          <div className="inline-block bg-white px-8 py-4 rounded-full shadow-lg">
            <div className="flex items-center justify-center text-[#4B9CD3]">
              <Mail className="w-6 h-6 mr-3" />
              <a href="mailto:cimhs2024@gmail.com" className="text-lg hover:text-[#13294B] transition-colors">
                cimhs2024@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#13294B] text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg font-semibold mb-4">
            Sponsored by Cambridge Health Alliance | PsychSIGN | Tufts
          </p>
          <p className="text-sm text-[#4B9CD3]">
            © 2025 Coalition for an Integrated Mental Health System. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Website;